import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import { useMyDIDs } from '../../api/hooks';
import { useWeb3 } from '../../contexts/Web3Context';
import { useAgentTrustRegistry } from '../../hooks/useAgentTrustRegistry';
import { describeTxError } from '../../lib/txError';
import { useNetwork } from '../../contexts/NetworkContext';

interface AgentFormData {
  name: string;
  description: string;
  modelProvider: string;
  modelName: string;
  modelVersion: string;
  capabilities: string[];
  delegationActions: string[];
  delegationResources: string[];
  budgetAmount: string;
  budgetCurrency: string;
  budgetPeriod: string;
  expiryDate: string;
  autonomyLevel: string;
  constraints: string;
}

const predefinedCapabilityIds = [
  'shopping', 'price-comparison', 'order-placement', 'data-analysis', 'document-review',
  'code-review', 'customer-support', 'scheduling', 'email-management', 'research',
  'translation', 'content-creation',
];

const autonomyLevelToInt: Record<string, number> = {
  Intern: 0,
  Junior: 1,
  Senior: 2,
  Principal: 3,
};

const autonomyLevels = [
  { id: 'Intern', permissions: ['read_data', 'call_api'], color: 'slate' },
  { id: 'Junior', permissions: ['read_data', 'write_data', 'call_api', 'call_external_api', 'manage_credentials'], color: 'blue' },
  { id: 'Senior', permissions: ['read_data', 'write_data', 'delete_data', 'call_api', 'call_external_api', 'issue_delegation', 'execute_transaction', 'manage_credentials', 'access_pii', 'sign_documents'], color: 'emerald' },
  { id: 'Principal', permissions: ['read_data', 'write_data', 'delete_data', 'call_api', 'call_external_api', 'issue_delegation', 'sub_delegate', 'execute_transaction', 'modify_config', 'access_pii', 'approve_action', 'deploy_agent', 'manage_credentials', 'sign_documents'], color: 'purple' },
];

const budgetPeriods = ['per_action', 'daily', 'weekly', 'monthly', 'total'];

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  const { t } = useTranslation('agents');
  const stepLabels = t('deploy.stepLabels', { returnObjects: true }) as string[];
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        const isActive = step === currentStep;
        const isComplete = step < currentStep;
        return (
          <div key={step} className="flex items-center gap-2">
            <div className={`flex size-8 items-center justify-center rounded-full text-xs font-bold transition-all ${
              isActive ? 'bg-primary text-white' :
              isComplete ? 'bg-emerald-500 text-white' :
              'bg-slate-100 text-text-faint'
            }`}>
              {isComplete ? (
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check</span>
              ) : step}
            </div>
            <span className={`hidden text-xs font-semibold sm:inline ${isActive ? 'text-primary' : isComplete ? 'text-emerald-600' : 'text-text-faint'}`}>
              {stepLabels[i]}
            </span>
            {i < totalSteps - 1 && <div className={`h-px w-8 ${isComplete ? 'bg-emerald-300' : 'bg-slate-200'}`}></div>}
          </div>
        );
      })}
    </div>
  );
}

function Step1Details({ data, onChange }: { data: AgentFormData; onChange: (d: Partial<AgentFormData>) => void }) {
  const { t } = useTranslation('agents');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-text-main">{t('deploy.step1.title')}</h2>
      <p className="text-sm text-text-muted">{t('deploy.step1.subtitle')}</p>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">
            {t('deploy.step1.nameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            onBlur={() => setTouched((tt) => ({ ...tt, name: true }))}
            placeholder={t('deploy.step1.namePlaceholder')}
            className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1 ${touched.name && !data.name.trim() ? 'border-red-300 focus:border-red-400 focus:ring-red-200' : 'border-border-subtle focus:border-primary focus:ring-primary'}`}
          />
          {touched.name && !data.name.trim() && (
            <p className="mt-1 text-xs text-red-600">{t('deploy.step1.nameRequired')}</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step1.descriptionLabel')}</label>
          <textarea
            value={data.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder={t('deploy.step1.descriptionPlaceholder')}
            rows={3}
            className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">
              {t('deploy.step1.providerLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.modelProvider}
              onChange={(e) => onChange({ modelProvider: e.target.value })}
              onBlur={() => setTouched((tt) => ({ ...tt, modelProvider: true }))}
              placeholder={t('deploy.step1.providerPlaceholder')}
              className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1 ${touched.modelProvider && !data.modelProvider.trim() ? 'border-red-300 focus:border-red-400 focus:ring-red-200' : 'border-border-subtle focus:border-primary focus:ring-primary'}`}
            />
            {touched.modelProvider && !data.modelProvider.trim() && (
              <p className="mt-1 text-xs text-red-600">{t('deploy.step1.providerRequired')}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step1.modelNameLabel')}</label>
            <input
              type="text"
              value={data.modelName}
              onChange={(e) => onChange({ modelName: e.target.value })}
              placeholder={t('deploy.step1.modelNamePlaceholder')}
              className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step1.modelVersionLabel')}</label>
            <input
              type="text"
              value={data.modelVersion}
              onChange={(e) => onChange({ modelVersion: e.target.value })}
              placeholder={t('deploy.step1.modelVersionPlaceholder')}
              className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Step2Capabilities({ data, onChange }: { data: AgentFormData; onChange: (d: Partial<AgentFormData>) => void }) {
  const { t } = useTranslation('agents');
  const toggleCapability = (id: string) => {
    const caps = data.capabilities.includes(id)
      ? data.capabilities.filter((c) => c !== id)
      : [...data.capabilities, id];
    onChange({ capabilities: caps });
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-text-main">{t('deploy.step2.title')}</h2>
      <p className="text-sm text-text-muted">{t('deploy.step2.subtitle')}</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {predefinedCapabilityIds.map((id) => {
          const selected = data.capabilities.includes(id);
          return (
            <button
              key={id}
              onClick={() => toggleCapability(id)}
              className={`rounded-xl border p-4 text-left transition-all ${
                selected
                  ? 'border-primary bg-blue-50/50 shadow-sm'
                  : 'border-border-subtle bg-white hover:border-slate-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex size-5 shrink-0 items-center justify-center rounded ${selected ? 'bg-primary' : 'border border-slate-300 bg-white'}`}>
                  {selected && <span className="material-symbols-outlined text-white" style={{ fontSize: 14 }}>check</span>}
                </div>
                <div>
                  <p className={`text-sm font-bold ${selected ? 'text-primary' : 'text-text-main'}`}>
                    {t(`deploy.step2.predefined.${id}.label`)}
                  </p>
                  <p className="mt-0.5 text-xs text-text-muted">{t(`deploy.step2.predefined.${id}.description`)}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-text-faint">{t('deploy.step2.selected', { count: data.capabilities.length })}</p>
    </div>
  );
}

function Step3Delegation({ data, onChange, jsonError, setJsonError }: { data: AgentFormData; onChange: (d: Partial<AgentFormData>) => void; jsonError: string; setJsonError: (e: string) => void }) {
  const { t } = useTranslation('agents');
  const [actionsRaw, setActionsRaw] = useState(data.delegationActions.join(', '));
  const [resourcesRaw, setResourcesRaw] = useState(data.delegationResources.join(', '));

  const commitActions = (raw: string) => {
    onChange({ delegationActions: raw.split(',').map((s) => s.trim()).filter(Boolean) });
  };
  const commitResources = (raw: string) => {
    onChange({ delegationResources: raw.split(',').map((s) => s.trim()).filter(Boolean) });
  };

  const handleConstraintsChange = (value: string) => {
    onChange({ constraints: value });
    if (value && value.trim()) {
      try { JSON.parse(value); setJsonError(''); }
      catch { setJsonError(t('deploy.step3.invalidJson')); }
    } else {
      setJsonError('');
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-text-main">{t('deploy.step3.title')}</h2>
      <p className="text-sm text-text-muted">{t('deploy.step3.subtitle')}</p>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.actionsLabel')}</label>
          <input
            type="text"
            value={actionsRaw}
            onChange={(e) => setActionsRaw(e.target.value)}
            onBlur={(e) => commitActions(e.target.value)}
            placeholder={t('deploy.step3.actionsPlaceholder')}
            className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.resourcesLabel')}</label>
          <input
            type="text"
            value={resourcesRaw}
            onChange={(e) => setResourcesRaw(e.target.value)}
            onBlur={(e) => commitResources(e.target.value)}
            placeholder={t('deploy.step3.resourcesPlaceholder')}
            className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.constraintsLabel')}</label>
          <textarea
            value={data.constraints}
            onChange={(e) => handleConstraintsChange(e.target.value)}
            placeholder={t('deploy.step3.constraintsPlaceholder')}
            rows={3}
            className={`w-full rounded-lg border px-3 py-2 font-mono text-sm transition-colors focus:outline-none focus:ring-1 ${jsonError ? 'border-red-300 focus:border-red-400 focus:ring-red-200' : 'border-border-subtle focus:border-primary focus:ring-primary'}`}
          />
          {jsonError && <p className="mt-1 text-xs text-red-600">{jsonError}</p>}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.budgetAmount')}</label>
            <input
              type="number"
              value={data.budgetAmount}
              onChange={(e) => onChange({ budgetAmount: e.target.value })}
              placeholder="200"
              className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.currency')}</label>
            <input
              type="text"
              value={data.budgetCurrency}
              onChange={(e) => onChange({ budgetCurrency: e.target.value })}
              placeholder="USD"
              className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.budgetPeriod')}</label>
            <select
              value={data.budgetPeriod}
              onChange={(e) => onChange({ budgetPeriod: e.target.value })}
              className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {budgetPeriods.map((p) => (
                <option key={p} value={p}>{p.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step3.expiry')}</label>
          <input
            type="date"
            value={data.expiryDate}
            // A delegation that expires in the past is never valid. `min` blocks
            // it in the picker; the submit-time guard catches typed/pasted dates
            // that bypass the widget.
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => onChange({ expiryDate: e.target.value })}
            className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {data.expiryDate && data.expiryDate < new Date().toISOString().slice(0, 10) && (
            <p className="mt-1 text-xs font-semibold text-red-600">
              Expiry date cannot be in the past.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Step4Autonomy({ data, onChange }: { data: AgentFormData; onChange: (d: Partial<AgentFormData>) => void }) {
  const { t } = useTranslation('agents');
  const colorMap: Record<string, { bg: string; border: string; text: string; ring: string }> = {
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', ring: 'ring-slate-300' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', ring: 'ring-blue-300' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', ring: 'ring-emerald-300' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', ring: 'ring-purple-300' },
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-text-main">{t('deploy.step4.title')}</h2>
      <p className="text-sm text-text-muted">{t('deploy.step4.subtitle')}</p>

      <div className="space-y-3">
        {autonomyLevels.map((level) => {
          const selected = data.autonomyLevel === level.id;
          const colors = colorMap[level.color];
          return (
            <button
              key={level.id}
              onClick={() => onChange({ autonomyLevel: level.id })}
              className={`w-full rounded-xl border p-4 text-left transition-all ${
                selected
                  ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}`
                  : 'border-border-subtle bg-white hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className={`text-sm font-bold ${selected ? colors.text : 'text-text-main'}`}>{t(`deploy.step4.levels.${level.id}.label`)}</span>
                  <p className="mt-0.5 text-xs text-text-muted">{t(`deploy.step4.levels.${level.id}.description`)}</p>
                </div>
                <div className={`flex size-5 shrink-0 items-center justify-center rounded-full ${
                  selected ? 'bg-primary' : 'border border-slate-300'
                }`}>
                  {selected && <span className="material-symbols-outlined text-white" style={{ fontSize: 14 }}>check</span>}
                </div>
              </div>

              {selected && (
                <div className="mt-3 border-t border-border-subtle pt-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-text-faint">{t('deploy.step4.permissionsLabel')}</p>
                  <div className="flex flex-wrap gap-1">
                    {level.permissions.map((perm) => (
                      <span key={perm} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${colors.bg} ${colors.text}`}>
                        {perm.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step5Review({ data }: { data: AgentFormData }) {
  const { t } = useTranslation('agents');
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-text-main">{t('deploy.step5.title')}</h2>
      <p className="text-sm text-text-muted">{t('deploy.step5.subtitle')}</p>

      <div className="space-y-4">
        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step5.agentDetails')}</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="font-semibold text-text-muted">{t('deploy.step5.name')}</span></div>
            <div className="font-semibold text-text-main">{data.name || '-'}</div>
            <div><span className="font-semibold text-text-muted">{t('deploy.step5.model')}</span></div>
            <div className="font-semibold text-text-main">{data.modelProvider} / {data.modelName} ({data.modelVersion || t('deploy.step5.modelLatest')})</div>
          </div>
          {data.description && <p className="mt-2 text-xs text-text-muted">{data.description}</p>}
        </div>

        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step5.capabilitiesCount', { count: data.capabilities.length })}</h3>
          <div className="flex flex-wrap gap-1">
            {data.capabilities.map((c) => (
              <span key={c} className="rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{c}</span>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step5.delegationScope')}</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="font-semibold text-text-muted">{t('deploy.step5.actions')}</span></div>
            <div className="font-semibold text-text-main">{data.delegationActions.join(', ') || '-'}</div>
            <div><span className="font-semibold text-text-muted">{t('deploy.step5.resources')}</span></div>
            <div className="font-semibold text-text-main">{data.delegationResources.join(', ') || '*'}</div>
            <div><span className="font-semibold text-text-muted">{t('deploy.step5.budget')}</span></div>
            <div className="font-semibold text-text-main">
              {data.budgetAmount ? `${data.budgetAmount} ${data.budgetCurrency} / ${data.budgetPeriod.replace('_', ' ')}` : t('deploy.step5.noBudget')}
            </div>
            <div><span className="font-semibold text-text-muted">{t('deploy.step5.expiryLabel')}</span></div>
            <div className="font-semibold text-text-main">{data.expiryDate || t('deploy.step5.expiryNotSet')}</div>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.step5.autonomyLevel')}</h3>
          <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-sm font-bold text-blue-700">{data.autonomyLevel}</span>
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ agentDID, agentKey, txResult, chainError }: { agentDID: string; agentKey?: string; txResult: { txHash: string; blockNumber: number } | null; chainError?: string }) {
  const { t } = useTranslation('agents');
  const explorerBase = import.meta.env.VITE_BLOCK_EXPLORER_URL ?? 'https://explorer.ab.testnet.adifoundation.ai';
  // Copy needs visible confirmation — without it the click looks like a no-op.
  const [copied, setCopied] = useState(false);
  const copyAgentDID = () => {
    navigator.clipboard.writeText(agentDID);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const [keyCopied, setKeyCopied] = useState(false);
  const copyAgentKey = () => {
    if (!agentKey) return;
    navigator.clipboard.writeText(agentKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  return (
    <div className="flex flex-col items-center py-12">
      <div className="flex size-20 items-center justify-center rounded-full bg-emerald-50">
        <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 40, fontVariationSettings: "'FILL' 1" }}>check_circle</span>
      </div>
      <h2 className="mt-4 text-xl font-bold text-text-main">{t('deploy.success.title')}</h2>
      <p className="mt-2 text-sm text-text-muted">
        {txResult
          ? 'Agent registered on-chain and off-chain.'
          : chainError
            ? 'Agent registered off-chain. The on-chain registration did not go through.'
            : 'Agent registered off-chain. Connect MetaMask to register on-chain.'}
      </p>
      {chainError && (
        <div className="mt-4 w-full max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{chainError}</div>
      )}

      <div className="mt-6 w-full max-w-lg space-y-4">
        {txResult && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: 18 }}>link</span>
              <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700">On-chain confirmation</h3>
            </div>
            <div className="text-xs text-emerald-800 font-mono">Block #{txResult.blockNumber}</div>
            <a
              href={`${explorerBase}/tx/${txResult.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block truncate text-xs text-emerald-700 underline font-mono"
            >
              {txResult.txHash}
            </a>
          </div>
        )}

        {agentKey && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-700">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>key</span>
                Agent private key (hex)
              </h3>
              <button
                onClick={copyAgentKey}
                className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold text-primary hover:bg-blue-50"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{keyCopied ? 'check' : 'content_copy'}</span>
                {keyCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="mb-2 text-xs text-amber-800">
              The agent signs its payments and other requests with this key. It is shown only once and never stored, so save it now.
            </p>
            <code className="block break-all rounded-lg bg-white p-2 font-mono text-xs text-text-main">{agentKey}</code>
          </div>
        )}

        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-text-faint">{t('deploy.success.agentDid')}</h3>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-text-main">{agentDID}</code>
            <button
              className="rounded-lg border border-border-subtle p-2 text-text-faint hover:bg-slate-50 hover:text-text-main"
              onClick={copyAgentDID}
              title={copied ? 'Copied!' : 'Copy Agent DID'}
              aria-label="Copy Agent DID"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                {copied ? 'check' : 'content_copy'}
              </span>
            </button>
            {copied && <span className="text-xs font-semibold text-emerald-600">Copied!</span>}
          </div>
        </div>

        <div className="flex justify-center gap-3">
          <Link to={`/ai-agents/${agentDID}`} className="btn-primary">
            {t('deploy.success.viewDetails')}
          </Link>
          <Link to="/ai-agents" className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-semibold text-text-muted hover:bg-slate-50">
            {t('deploy.success.back')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function DeployAgent() {
  const { t } = useTranslation('agents');
  const { data: myDIDs } = useMyDIDs();
  const { account, isConnected, connect, isWrongNetwork, switchNetwork, provider } = useWeb3();
  const { selected } = useNetwork();
  const { registerAgent: registerOnChain, pending: txPending, isReady: contractReady } = useAgentTrustRegistry();
  const [step, setStep] = useState(1);
  const [deployed, setDeployed] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [agentDID, setAgentDID] = useState('');
  const [agentKey, setAgentKey] = useState('');
  const [txResult, setTxResult] = useState<{ txHash: string; blockNumber: number } | null>(null);
  const [chainError, setChainError] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const totalSteps = 5;

  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    description: '',
    modelProvider: '',
    modelName: '',
    modelVersion: '',
    capabilities: [],
    delegationActions: [],
    delegationResources: [],
    budgetAmount: '',
    budgetCurrency: 'USD',
    budgetPeriod: 'weekly',
    expiryDate: '',
    autonomyLevel: 'Intern',
    constraints: '',
  });

  const updateForm = (partial: Partial<AgentFormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  const handleDeploy = async () => {
    const operator = selectedOperator || (myDIDs?.[0]?.did ?? '');
    if (!operator) {
      setDeployError('You need a DID to act as operator. Create one first.');
      return;
    }
    setDeploying(true);
    setDeployError('');
    try {
      const agentCard = {
        provider: formData.modelProvider,
        name: formData.modelName,
        version: formData.modelVersion,
        capabilities: formData.capabilities,
        autonomyLevel: autonomyLevelToInt[formData.autonomyLevel] ?? 0,
      };

      // Register in the API first to get the agent DID.
      const result = await apiFetch<{ id: string; did: string; privateKeyHex?: string }>('/agents', {
        method: 'POST',
        body: JSON.stringify({
          operator,
          modelInfo: {
            provider: formData.modelProvider,
            name: formData.modelName,
            version: formData.modelVersion,
          },
          capabilities: formData.capabilities.map((name) => ({
            name,
            description: name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          })),
          autonomyLevel: autonomyLevelToInt[formData.autonomyLevel] ?? 0,
        }),
      });
      const did = result.id ?? result.did ?? '';
      setAgentDID(did);
      setAgentKey(result.privateKeyHex ?? '');

      // On-chain registration via MetaMask (non-fatal: off-chain record is already saved).
      if (contractReady && did) {
        try {
          const onChain = await registerOnChain(did, operator, agentCard);
          setTxResult(onChain);
        } catch (chainErr) {
          console.warn('On-chain registration failed (off-chain record intact):', chainErr);
          setChainError(await describeTxError(chainErr, { provider, account, chain: selected }));
        }
      }

      setDeployed(true);
    } catch (e) {
      setDeployError(e instanceof Error ? e.message : t('deploy.deployFailed'));
    } finally {
      setDeploying(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.name.trim() !== '' && formData.modelProvider.trim() !== '';
      case 2: return formData.capabilities.length > 0;
      case 3: return !jsonError;
      case 4: return formData.autonomyLevel !== '';
      case 5: return true;
      default: return false;
    }
  };

  if (deployed) {
    return (
      <div className="space-y-6">
        <nav className="flex items-center gap-2 text-xs text-text-faint">
          <Link to="/ai-agents" className="hover:text-primary">{t('deploy.breadcrumb')}</Link>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
          <span className="font-semibold text-text-main">{t('deploy.current')}</span>
        </nav>
        <SuccessScreen agentDID={agentDID} agentKey={agentKey} txResult={txResult} chainError={chainError} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-xs text-text-faint">
        <Link to="/ai-agents" className="hover:text-primary">{t('deploy.breadcrumb')}</Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <span className="font-semibold text-text-main">{t('deploy.current')}</span>
      </nav>

      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('deploy.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('deploy.subtitle')}</p>
      </div>

      <StepIndicator currentStep={step} totalSteps={totalSteps} />

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        {step === 1 && <Step1Details data={formData} onChange={updateForm} />}
        {step === 2 && <Step2Capabilities data={formData} onChange={updateForm} />}
        {step === 3 && <Step3Delegation data={formData} onChange={updateForm} jsonError={jsonError} setJsonError={setJsonError} />}
        {step === 4 && <Step4Autonomy data={formData} onChange={updateForm} />}
        {step === 5 && (
          <>
            <Step5Review data={formData} />

            {/* MetaMask / on-chain status banner */}
            <div className="mt-4 rounded-xl border p-4 text-sm">
              {!isConnected ? (
                <div className="flex items-center justify-between">
                  <span className="text-amber-700">Connect MetaMask to register on-chain in the AgentTrustRegistry.</span>
                  <button onClick={connect} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
                    Connect Wallet
                  </button>
                </div>
              ) : isWrongNetwork ? (
                <div className="flex items-center justify-between">
                  <span className="text-red-700">Wrong network — switch to ADI to register on-chain.</span>
                  <button onClick={switchNetwork} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700">
                    Switch Network
                  </button>
                </div>
              ) : contractReady ? (
                <div className="flex items-center gap-2 text-emerald-700">
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>account_balance_wallet</span>
                  <span><span className="font-bold">MetaMask connected</span> — agent will be registered on-chain as {account?.slice(0, 8)}…{account?.slice(-4)}</span>
                </div>
              ) : (
                <div className="text-slate-500">MetaMask connected but AgentTrustRegistry address not configured — registering off-chain only.</div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-border-subtle bg-white p-4">
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-text-faint">
                Operator DID <span className="text-red-500">*</span>
              </label>
              {myDIDs && myDIDs.length > 0 ? (
                <select
                  value={selectedOperator || myDIDs[0].did}
                  onChange={(e) => setSelectedOperator(e.target.value)}
                  className="w-full rounded-lg border border-border-subtle px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {myDIDs.map((d) => (
                    <option key={d.did} value={d.did}>{d.did}</option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-red-500">No DID found. <Link to="/dids" className="underline">Create a DID</Link> first.</p>
              )}
            </div>
          </>
        )}
      </div>

      {deployError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{deployError}</div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-semibold text-text-muted transition-colors hover:bg-slate-50 disabled:opacity-40"
        >
          {t('deploy.back')}
        </button>
        {step < totalSteps ? (
          <button
            onClick={() => setStep((s) => Math.min(totalSteps, s + 1))}
            disabled={!canProceed()}
            className="btn-primary disabled:opacity-40"
          >
            {t('deploy.continue')}
          </button>
        ) : (
          <button
            onClick={handleDeploy}
            disabled={deploying}
            className="rounded-lg bg-emerald-600 px-6 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-50"
          >
            <span className="material-symbols-outlined mr-1" style={{ fontSize: 16 }}>rocket_launch</span>
            {deploying ? t('deploy.deploying') : t('deploy.deploy')}
          </button>
        )}
      </div>
    </div>
  );
}
